<template>
  <ProjectPerformersInfo v-if="tab === 'performers'" title="Проекты" :projectData="this.tempProjectData"
    :basicData="this.tempBasicData" @close="handleClose" :tab="tab" @change-tab="changeTab" />

  <ProjectsInfo v-else-if="tab === 'projects_overview' && this.selectedData" title="Проекты"
    :selectedData="this.selectedData" :tab="tab" @change-tab="changeTab" @show-performers="handleShowPerformers"
    @back-to-projects="handleBackToProjects" />

  <div v-else-if="isRestoringData" style="display: flex; justify-content: center; align-items: center; height: 400px;">
    <q-spinner color="primary" size="3em" />
  </div>

  <div v-else>
    <div style="display: flex; align-items: center; gap: 10px; width: 100%; margin-bottom: 10px;">
      <div class="top-bar">
        <PageTitle hideMarginLeft> Группа проектов </PageTitle>
        <CustomButton class="top-bar__btn" @click="this.startTourGuide">
          <img src="/img/icons/help.svg" alt="help" />
          <span>Подсказка</span>
        </CustomButton>
      </div>
    </div>

    <div class="header">
      <div class="header__filters">
        <div class="header__item header__item--resident">
          <CustomSelect
            fullWidth
            :options="this.residentsOptions"
            label="ЖК"
            option-label-key="resident_name"
            option-value-key="resident_id"
            @change="value => this.onChange('resident_id', value)"
            :value="this.filters.resident_id"
          />
        </div>
        <div class="header__item header__item--project-type">
          <CustomSelect
            fullWidth
            :options="this.projectTypesOptions"
            label="Тип проекта"
            option-label-key="project_type_name"
            option-value-key="project_type_id"
            @change="value => this.onChange('project_type_id', value)"
            :value="this.filters.project_type_id"
          />
        </div>
        <div class="header__item header__item--search">
          <CustomInput
            :value="this.projectIdSearch"
            :isSearch="true"
            placeholder="Поиск по ID проекта"
            class="project-id-search"
            v-debounce:300="this.onSearchChange"
          />
        </div>
      </div>
      <div id="create">
        <CustomButton class="header__create-btn" nowrap @click="this.onAddResidentClick">
          <span>Создать</span>
          <q-icon name="add" class="header__create-btn-icon" size="16px" />
        </CustomButton>
      </div>
    </div>

    <CustomTable title="Проекты" :rowsData="this.filteredRowsData" :columns="this.projectsColumns" :loading="isFetching"
      :small="true" :show-search="true" :custom-body="true" :sticky-header="true" :update-data="this.getData"
      :height="300">
      <template #customBody="props">
        <q-tr>
          <custom-table-cell 
            v-if="residentRowspans[props.rowIndex] > 0"
            align="left" 
            vertical-align="center" 
            id="project"
            :rowspan="residentRowspans[props.rowIndex]"
            class="resident-cell"
          >
            <div class="resident-name">
              {{ props.row.resident_name }}
            </div>
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <q-chip
              :label="props.row.project_type_name"
              :color="getProjectTypeColor(props.row.project_type_name)"
              text-color="white"
              clickable
              @click="this.selectResident(props.row)"
              class="project-type-chip"
              icon="svguse:/img/icons/slice.svg#folder|0 -2 18 18"
            >
              <q-tooltip anchor="top middle" self="bottom middle">
                Открыть проекты {{ props.row.project_type_name }}
              </q-tooltip>
            </q-chip>
          </custom-table-cell>

          <custom-table-cell 
            align="left" 
            vertical-align="center"
            :class="{ 'error-cell': hasMultipleProjects(props.row.naimenovanie) }"
          >
            <div v-if="Array.isArray(props.row.naimenovanie) && props.row.naimenovanie.length > 0" class="usp-cell-content">
              <q-icon 
                v-if="hasMultipleProjects(props.row.naimenovanie)"
                name="warning"
                color="red"
                size="24px"
                class="error-icon"
              >
                <q-tooltip 
                  anchor="top middle" 
                  self="bottom middle"
                  class="error-tooltip"
                >
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <q-icon name="warning" size="20px" />
                    <span>Возможно ошибочная привязка проекта!</span>
                  </div>
                </q-tooltip>
              </q-icon>
              <div class="usp-names">
                <div v-for="(name, index) in props.row.naimenovanie" :key="index" style="margin-bottom: 2px;">
                  {{ name || '-' }}
                </div>
              </div>
            </div>
            <span v-else>-</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <div v-if="Array.isArray(props.row.project_ids) && props.row.project_ids.length > 0">
              {{ props.row.project_ids.join(', ') }}
            </div>
            <span v-else>-</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <div v-if="Array.isArray(props.row.block_names) && props.row.block_names.length > 0">
              {{ props.row.block_names.filter(b => b).join(', ') || '-' }}
            </div>
            <span v-else>-</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ formatDate(props.row.min_start_date) }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ formatDate(props.row.max_finish_date) }}
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <TourGuideManager ref="tourGuideManager" :steps="tourSteps" :auto-start="false" :show-overlay="true"
      :allow-skip="true" :highlight-padding="8" :labels="tourGuideLabels" />
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import BackBtn from "@/components/common/BackBtn";
import CheckOrClose from "@/components/common/CheckOrClose";
import Clickable from "@/components/common/Clickable";
import CustomIcon from "@/components/common/CustomIcon";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import PageTitle from '@/components/common/PageTitle.vue';
import ProgressBar from "@/components/common/ProgressBar";
import CustomSelect from "@/components/form/CustomSelect";
import CustomInput from "@/components/form/CustomInput";
import { MODAL_NAMES } from "@/constants";
import { useAppStore } from "@/services/store/app";
import ProjectPerformersInfo from "@/views/ProjectsEntry/ProjectPerformersInfo";
import ProjectsInfo from "@/views/ProjectsEntry/ProjectsInfo";
import {
  getAllProjects,
  getResidentDetailedInfo,
  projectsColumns,
  residentsTourGuide,
  selectedProjectsColumns,
  updProject
} from "@/views/ProjectsEntry/services";
import CustomDropdown from "@/components/common/CustomDropdown";
import CustomButton from "@/components/common/CustomButton.vue";
import { formatDate } from "@/utils/formatDate";
import { TourGuideManager } from "v-tour-guide";
import { getProjectTypes, getResidentialSettingsList } from '../BuildingSettings/services';

export default {
  name: "ProjectsEntry",
  components: {
    ProjectPerformersInfo,
    ProjectsInfo,
    CheckOrClose,
    Clickable,
    CustomIcon,
    CustomTableCell,
    CustomTable,
    CustomSelect,
    CustomInput,
    CustomDropdown,
    PageTitle,
    CustomButton,
    ProgressBar,
    TourGuideManager,
    BackBtn
  },
  data() {
    return {
      projectsColumns,
      residentsTourGuide,
      selectedProjectsColumns,
      rowsData: [],
      projectIdSearch: '',
      isFetching: false,
      isRestoringData: false,
      tempBasicData: {},
      tempProjectData: {},
      isCommonInfoOpen: false,
      fetchingEntrances: false,
      entranceOptionsData: [],
      filters: {
        resident_id: '',
        project_type_id: '',
      },
      savedFilters: null,
      fetchingFilters: true,
      selectedData: null,
      residentsOptions: [],
      projectTypesOptions: [],
      tourGuideLabels: {
        next: 'Далее',
        previous: 'Назад',
        finish: 'Завершить'
      },
      tab: 'projects',
      tabOptions: [
        {
          label: 'Группа проектов',
          value: 'projects'
        },
        {
          label: 'Проекты',
          value: 'projects_overview'
        },
        {
          label: 'Исполнители',
          value: 'performers'
        }
      ],
    };
  },
  computed: {
    isResidentSelected() {
      return !!this.selectedData
    },
    selectedProjects() {
      if (!this.isResidentSelected) return []
      return this.rowsData.filter(
        p => p.resident_id === this.selectedData.resident_id &&
          p.project_type_id === this.selectedData.project_type_id
      )
    },
    tourSteps() {
      return this.residentsTourGuide.filter(step => {
        if (step.id === 'select-project' && this.rowsData.length === 0) {
          return false
        }
        return true
      })
    },
    filteredRowsData() {
      if (!this.projectIdSearch) {
        return this.rowsData;
      }
      
      const searchValue = this.projectIdSearch.trim();
      
      return this.rowsData.filter(row => {
        // Поиск только по ID проектов
        if (Array.isArray(row.project_ids)) {
          return row.project_ids.some(id => String(id) === searchValue);
        }
        return false;
      });
    },
    residentRowspans() {
      const rowspans = {};
      const residentGroups = {};
      
      // Подсчитываем количество строк для каждого ЖК в отфильтрованных данных
      this.filteredRowsData.forEach((row, index) => {
        const residentName = row.resident_name;
        if (!residentGroups[residentName]) {
          residentGroups[residentName] = [];
        }
        residentGroups[residentName].push(index);
      });
      
      // Устанавливаем rowspan для первой строки каждой группы
      Object.values(residentGroups).forEach(indices => {
        indices.forEach((index, i) => {
          if (i === 0) {
            rowspans[index] = indices.length;
          } else {
            rowspans[index] = 0;
          }
        });
      });
      
      return rowspans;
    }
  },
  methods: {
    formatDate,
    getProjectTypeColor(typeName) {
      const colorMap = {
        'Свай и фундаменты': 'brown-7',
        'Каркас - Плиты перекрытия': 'blue-grey-7',
        'Внутренние перегородки': 'orange-7',
        'Каркас - кирпичная кладка': 'deep-orange-7',
        'Стяжка': 'grey-7',
        'Черновая': 'indigo-6',
        'Кровля': 'red-7',
        'Заполнение проемов': 'teal-7'
      };
      return colorMap[typeName] || 'blue-grey-7';
    },
    hasMultipleProjects(naimenovanie) {
      if (!Array.isArray(naimenovanie)) return false;
      // Фильтруем не-null значения и считаем уникальные
      const validProjects = naimenovanie.filter(name => name);
      const uniqueProjects = new Set(validProjects);
      return uniqueProjects.size > 1;
    },
    changeTab(tab) {
      this.tab = tab

      const query = { ...this.$route.query, tab }

      // При переходе на вкладку исполнителей сохраняем project_id и базовые параметры
      // в URL, чтобы после обновления страницы можно было восстановить данные.
      if (tab === 'performers') {
        if (this.tempProjectData?.project_id) {
          query.project_id = this.tempProjectData.project_id
        }
        if (this.tempBasicData?.resident_id) {
          query.resident_id = this.tempBasicData.resident_id
        }
        if (this.tempBasicData?.project_type_id) {
          query.project_type_id = this.tempBasicData.project_type_id
        }
      }

      this.$router.replace({ query })
    },
    async initFiltersFromRoute() {
      this.fetchingFilters = true
      const { resident_id, project_type_id, tab, project_id } = this.$route.query

      this.filters = {
        resident_id: resident_id ? Number(resident_id) : '',
        project_type_id: project_type_id ? Number(project_type_id) : '',
      }

      if (resident_id && project_type_id && tab === 'projects_overview') {
        await this.restoreSelectedData()
      }

      // Восстановление данных для вкладки "Исполнители проекта" после обновления страницы
      if (tab === 'performers' && project_id) {
        this.tempProjectData = {
          project_id: Number(project_id),
        }
        const numericResidentId = resident_id ? Number(resident_id) : null
        const numericProjectTypeId = project_type_id ? Number(project_type_id) : null

        this.isRestoringData = true

        let rows = []
        // Если есть ЖК и тип проекта — берём подробный список проектов, как во вкладке "Проекты"
        if (numericResidentId && numericProjectTypeId) {
          rows = await getResidentDetailedInfo({
            resident_id: numericResidentId,
            project_type_id: numericProjectTypeId,
          }) || []
        }

        this.rowsData = rows

        const found = rows.find(
          row => row.project_id === Number(project_id)
        )

        if (found) {
          this.tempBasicData = {
            project_id: found.project_id,
            project_name: found.project_name,
            resident_id: found.resident_id,
            resident_name: found.resident_name,
            project_type_id: found.project_type_id,
            project_type_name: found.project_type_name,
            entrances: found.entrances,
          }
        }

        this.isRestoringData = false
      }

      if (tab) {
        const isTabValid = !!this.tabOptions.find(tabOption => tabOption.value === tab)
        if (isTabValid) {
          this.tab = tab
        }
      }
      this.fetchingFilters = false
    },
    startTourGuide() {
      this.$nextTick(() => {
        this.$refs.tourGuideManager.startTourGuide();
      });
    },
    async selectResident(row) {
      this.savedFilters = { ...this.filters }

      this.selectedData = row
      this.filters = {
        resident_id: row.resident_id,
        project_type_id: row.project_type_id
      }
      this.changeTab('projects_overview')
    },
    handleShowPerformers(data) {
      this.tempProjectData = data.projectData
      this.tempBasicData = data.basicData
      this.changeTab('performers')
    },
    async handleBackToProjects() {
      this.selectedData = null

      if (this.savedFilters) {
        this.filters = { ...this.savedFilters }
        this.savedFilters = null
      } else {
        this.filters = {
          resident_id: '',
          project_type_id: '',
        }
      }

      this.changeTab('projects')
      await this.getData()
    },
    onAddResidentClick() {
      const appStore = useAppStore()
      appStore.showModal(MODAL_NAMES.AddResidentEntry, {
        title: 'Занести в проект',
        onSubmit: async (body) => {
          await this.onAddSubmit(body)
          this.getData()
        },
      })
    },
    async getData() {
      this.isFetching = true;
      const res = await getAllProjects(this.filters);
      this.isFetching = false;
      this.rowsData = res || []
    },
    async getSelectsOptions() {
      const residentsOptions = await getResidentialSettingsList();
      const projectTypesOptions = await getProjectTypes();

      this.residentsOptions = !residentsOptions ? [] : residentsOptions;
      this.projectTypesOptions = !projectTypesOptions ? [] : projectTypesOptions;
    },
    onChange(key, value) {
      this.filters[key] = value;
      if (key === 'resident_id') {
        this.filters = { resident_id: value, project_type_id: '' }
      }
      if (key === 'project_type_id') {
        this.filters.project_entrance_id = ''
      }
      this.getData();
    },
    async restoreSelectedData() {
      this.isRestoringData = true

      this.savedFilters = { resident_id: '', project_type_id: '' }

      await this.getData()

      const found = this.rowsData.find(
        row => row.resident_id === this.filters.resident_id &&
          row.project_type_id === this.filters.project_type_id
      )

      if (found) {
        this.selectedData = found
      } else {
        this.changeTab('projects')
      }

      this.isRestoringData = false
    },
    handleClose() {
      this.getData();
      this.isCommonInfoOpen = false;
    },
    onSearchChange(value) {
      this.projectIdSearch = value || ''
    },
    async onAddSubmit(body) {
      const appStore = useAppStore()

      const res = await updProject(body, this.filters);
      if (!res) return;
      this.rowsData = res;
      this.showSucceedSnackbar();
      appStore.closeModal();
    },
    async onUpdateSubmit(body) {
      const appStore = useAppStore()

      const res = await updProject(body, this.filters);
      if (!res) return;
      this.rowsData = res;
      this.showSucceedSnackbar();
      appStore.closeModal();
    },
  },
  mounted() {
    this.initFiltersFromRoute()
    this.getSelectsOptions();
    this.getData();
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
  watch: {
    filters: {
      deep: true,
      handler(newVal) {
        // Сохраняем существующие query-параметры (в том числе project_id для вкладки исполнителей)
        const query = { ...this.$route.query, tab: this.tab }

        if (newVal.resident_id) {
          query.resident_id = newVal.resident_id
        }

        if (newVal.project_type_id) {
          query.project_type_id = newVal.project_type_id
        }

        this.$router.replace({ query })
      }
    }
  }
};
</script>

<style scoped lang="scss">
.top-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__btn {
    background-color: transparent;
    width: auto;

    span {
      margin-left: 8px;
      color: #373D48;
      font-size: 20px;
      font-weight: 600;
      line-height: 20px;
    }
  }

  :deep(.title) {
    margin-bottom: 0 !important;
  }
}

.header {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  justify-content: space-between;
  align-items: end;

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 20px;
  }

  &__item {
    &--resident {
      flex: 1 1 300px;
      min-width: 300px;
      max-width: 480px;

      :deep(.custom-select__wrapper) {
        width: 100%;
      }
    }

    &--project-type {
      flex: 2 1 400px;
      min-width: 400px;
      max-width: 640px;

      :deep(.custom-select__wrapper) {
        width: 100%;
      }
    }

    &--search {
      flex: 0 1 280px;
      min-width: 220px;
      max-width: 360px;
    }
  }

  &__card {
    background-color: #F2F5F8;
    padding: 11px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 22px;
    color: #373D48;
    font-size: 16px;
    line-height: 16px;
    font-weight: 400;
  }

  &__create-btn {
    width: auto;
    height: 46px;
  }

  &__create-btn-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #fff;
    color: #0060fe;
  }

  :deep(.q-btn__content) {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  @media (max-width: 600px) {
    &__item--resident,
    &__item--project-type,
    &__item--search {
      flex: 1 1 100%;
      max-width: none;
    }
  }
}

.smr-btn {
  font-size: 12px;
  width: auto;
  height: 32px;

  &--disabled {
    background: #D1D5DB;
    color: #373D48 !important;
  }
}

.actions-btn {
  font-size: 12px;
  width: 100%;
  height: 32px;
}

.actions-btn--divide {
  background: #21830b;
  color: #fff;
}

.actions-btn--copy {
  background: #0060fe;
  color: #fff;
}

.item {
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all .3s;
  padding: 5px 10px;
  font-size: 14px;
  color: #404040;
  line-height: 20px;
  min-height: 30px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
}

.fill-yellow {
  background-color: #fff3cd;
  color: #856404;
  font-weight: bold;
}

.fill-green {
  background-color: #d4edda;
  color: #155724;
  font-weight: bold;
}

.text-lg span {
  font-size: "16px";
}

.doc-link {
  cursor: pointer;
  text-decoration: underline;
  color: #666;
}

.doc-link:hover {
  color: #666;
  text-decoration: none;
}

.simple-tooltip {
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.tooltip-title {
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 13px;
  color: #fff;
}

.signer-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.signer-row:last-child {
  margin-bottom: 0;
}

.signer-name {
  font-size: 13px;
  margin: 0;
}

.signer-icon {
  font-size: 16px;
}
</style>

<style>
.custom-tour-tooltip {
  background-color: #ffffff !important;
  color: #373D48 !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
}

.custom-tour-header {
  font-size: 22px !important;
  font-weight: 600 !important;
}

.custom-tour-content {
  font-size: 14px !important;
}

.custom-action-btn {
  font-size: 14px !important;
  background-color: transparent !important;
  color: #373D48 !important;
  padding: 6px 16px !important;
  border: 1px solid #9CA3AF !important;
  border-radius: 4px !important;
  margin-top: 6px !important;
}

.custom-tour-actions {
  display: flex;
  gap: 8px;

  div {
    display: flex;
    gap: 8px;
  }
}


.entrance-selector {
  width: 100%;

  &__label {
    font-size: 13px;
    line-height: 13px;
    margin-bottom: 8px;
    color: #262626;
  }
}

.resident-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
}

.project-type-chip {
  font-size: 13px !important;
  font-weight: 600 !important;
  padding: 8px 16px !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  
  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
  }
  
  &:active {
    transform: translateY(0) !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
  }
  
  :deep(.q-icon) {
    font-size: 18px !important;
  }
}

.resident-cell {
  border-right: 1px solid #e0e0e0 !important;
  background-color: #fafafa !important;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: #e0e0e0;
    pointer-events: none;
  }
}

:deep(tr:hover .resident-cell) {
  background-color: #f5f5f5 !important;
}

.error-cell {
  background-color: #ffebee !important;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #f44336;
  }
}

.usp-cell-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-icon {
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.usp-names {
  flex: 1;
}

.error-tooltip {
  background-color: #f44336 !important;
  color: white !important;
  font-size: 13px !important;
  padding: 8px 12px !important;
  font-weight: 500 !important;
}

.project-id-search {
  
  :deep(.q-field) {
    height: 46px;
  }
  
  :deep(.q-field__control) {
    height: 46px !important;
    min-height: 46px !important;
  }
  
  :deep(.q-field__label) {
    font-size: 13px;
    line-height: 13px;
    top: 14px;
  }
  
  :deep(.q-field__native) {
    padding-top: 0;
    padding-bottom: 0;
    line-height: 46px;
  }
  
  :deep(.q-field__marginal) {
    height: 46px;
  }
}
</style>
